import type { FC, ReactNode } from 'react'
import type { ContentBlock, MediaSlot as MediaSlotRow } from '@/types/content'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { MediaSlot } from '@/components/generals/media-slot'
import { SectionIntro } from '@/components/generals/section-intro'

interface PageHeroProps {
    block?: ContentBlock
    media?: MediaSlotRow
    slotKey?: string
    mediaClassName?: string
    children?: ReactNode
    className?: string
}

// Opening section of every sub page: eyebrow, H1 and lead from the CMS, optionally a media slot.
export const PageHero: FC<PageHeroProps> = ({
    block,
    media,
    slotKey,
    mediaClassName = 'aspect-[4/5]',
    children,
    className
}): ReactNode => {
    const withMedia = Boolean(slotKey)

    return (
        <section aria-labelledby='seite-titel' className={cn('relative overflow-hidden bg-bone pb-20 pt-14 sm:pb-28 sm:pt-20 lg:pb-32 lg:pt-24', className)}>
            <Container className={cn('grid gap-12 lg:gap-10', withMedia && 'lg:grid-cols-12 lg:items-end')}>
                <div className={cn('flex flex-col gap-10', withMedia ? 'lg:col-span-7' : 'max-w-5xl')}>
                    <SectionIntro block={block} as='h1' size='xl' headingId='seite-titel' motion='entrance' />

                    {children && (
                        <div className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-300 duration-1000 ease-brand'>
                            {children}
                        </div>
                    )}
                </div>

                {withMedia && slotKey && (
                    <div className='lg:col-span-5 animate-in fade-in fill-mode-both delay-200 duration-1000 ease-brand'>
                        <MediaSlot
                            slot={media}
                            slotKey={slotKey}
                            className={cn('w-full', mediaClassName)}
                            tone='dark'
                            priority
                            showCaption
                            captionClassName='text-smoke'
                        />
                    </div>
                )}
            </Container>
        </section>
    )
}
